import React, { useState, useEffect, useCallback } from 'react';
import './MealsManager.css';
import { DEPARTMENTS, titleCase } from './constants';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

function StaplesManager({ token, onStaplesUpdated, onAuthError, onFormStateChange }) {
  const [staples, setStaples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingStaple, setEditingStaple] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Notify parent when form state changes
  useEffect(() => {
    if (onFormStateChange) {
      const isFormOpen = showAddForm || editingStaple !== null;
      onFormStateChange(isFormOpen);
    }
  }, [showAddForm, editingStaple, onFormStateChange]);

  const loadStaples = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/staples`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during staples load - logging out');
        if (onAuthError) onAuthError();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setStaples(data.staples || []);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to load staples');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
    setLoading(false);
  }, [token, onAuthError]);

  // Load staples from API
  useEffect(() => {
    loadStaples();
  }, [loadStaples]);

  const handleSaveStaple = async (staple, isNew = false) => {
    setError('');
    try {
      const url = isNew
        ? `${API_URL}/api/staples`
        : `${API_URL}/api/staples/${encodeURIComponent(staple.originalName || staple.name)}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: staple.name,
          quantity: staple.quantity,
          quantity_type: staple.quantity_type,
          department: staple.department
        })
      });

      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during staple save - logging out');
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        await loadStaples();
        setEditingStaple(null);
        setShowAddForm(false);
        if (onStaplesUpdated) {
          onStaplesUpdated();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save staple');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  const handleDeleteStaple = async (stapleName) => {
    if (!window.confirm(`Are you sure you want to delete "${stapleName}"?`)) {
      return;
    }

    setError('');
    try {
      const response = await fetch(`${API_URL}/api/staples/${encodeURIComponent(stapleName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle JWT expiration
      if (response.status === 401) {
        console.log('JWT expired during staple delete - logging out');
        if (onAuthError) onAuthError();
        return;
      }

      if (response.ok) {
        await loadStaples();
        if (onStaplesUpdated) {
          onStaplesUpdated();
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete staple');
      }
    } catch (err) {
      setError('Failed to connect to server');
    }
  };

  if (loading) {
    return (
      <div className="meals-manager">
        <div className="loading-screen">
          <h2>Loading staples...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="meals-manager">
      <div className="meals-header">
        <h2>🛒 Manage Weekly Staples</h2>
        <p className="meals-description">
          Add items you regularly buy every week. These will be included in your shopping list.
        </p>
      </div>

      {error && (
        <div className="error-message">
          <p>❌ {error}</p>
          <button onClick={() => setError('')} className="dismiss-button">✕</button>
        </div>
      )}

      <div className="meals-actions">
        <button
          onClick={() => {
            setShowAddForm(true);
            setEditingStaple({
              name: '',
              quantity: '',
              quantity_type: '',
              department: 'grocery',
              isNew: true
            });
          }}
          className="add-meal-button"
        >
          + Add New Staple
        </button>
      </div>

      {(showAddForm || editingStaple) && (
        <StapleEditor
          staple={editingStaple}
          onSave={(staple) => handleSaveStaple(staple, editingStaple?.isNew)}
          onCancel={() => {
            setEditingStaple(null);
            setShowAddForm(false);
          }}
        />
      )}

      <div className="meals-list">
        {staples.length === 0 ? (
          <div className="no-meals">
            <p>You haven't added any weekly staples yet.</p>
            <p className="hint">
              Add items like milk, bread, eggs, or other things you buy every week.
            </p>
          </div>
        ) : (
          staples.map((staple, index) => (
            <StapleCard
              key={`${staple.name}-${index}`}
              staple={staple}
              onEdit={() => setEditingStaple({ ...staple, originalName: staple.name })}
              onDelete={() => handleDeleteStaple(staple.name)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function StapleCard({ staple, onEdit, onDelete }) {
  return (
    <div className="meal-card staple-card">
      <div className="meal-card-header">
        <h3 className="meal-card-title">{staple.name}</h3>
        <div className="meal-card-actions">
          <span className="ingredient-count">
            {staple.quantity} {staple.quantity_type}
          </span>
          <span className="staple-department">
            {titleCase(staple.department)}
          </span>
        </div>
      </div>
      <div className="meal-card-content staple-card-content">
        <div className="meal-card-buttons">
          <button onClick={onEdit} className="edit-button">✏️ Edit</button>
          <button onClick={onDelete} className="delete-button">🗑️ Delete</button>
        </div>
      </div>
    </div>
  );
}

function StapleEditor({ staple, onSave, onCancel }) {
  const [name, setName] = useState(staple?.name || '');
  const [quantity, setQuantity] = useState(staple?.quantity || '');
  const [quantityType, setQuantityType] = useState(staple?.quantity_type || '');
  const [department, setDepartment] = useState(staple?.department || 'grocery');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Staple name is required');
      return;
    }

    onSave({
      name: name.trim(),
      quantity: quantity.trim(),
      quantity_type: quantityType.trim(),
      department: department,
      originalName: staple?.originalName
    });
  };

  return (
    <div className="meal-editor-overlay">
      <div className="meal-editor staple-editor">
        <h2>{staple?.isNew ? 'Add New Staple' : 'Edit Staple'}</h2>
        
        {error && (
          <div className="editor-error">
            <p>❌ {error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="staple-name">Item Name</label>
            <input
              type="text"
              id="staple-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Milk, Bread, Eggs"
              autoFocus
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="staple-quantity">Quantity</label>
              <input
                type="text"
                id="staple-quantity"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="e.g., 1, 2, 0.5"
              />
            </div>

            <div className="form-group">
              <label htmlFor="staple-quantity-type">Unit</label>
              <input
                type="text"
                id="staple-quantity-type"
                value={quantityType}
                onChange={(e) => setQuantityType(e.target.value)}
                placeholder="e.g., gallon, loaf, dozen"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="staple-department">Department</label>
            <select
              id="staple-department"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
            >
              {DEPARTMENTS.map(dept => (
                <option key={dept} value={dept}>
                  {titleCase(dept)}
                </option>
              ))}
            </select>
          </div>

          <div className="editor-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Cancel
            </button>
            <button type="submit" className="save-button">
              Save Staple
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StaplesManager;
